import {
  FC,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getCurrentUser, revokeSession} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = useCallback(() => {
    revokeSession().catch(() => undefined)
    setAuth(undefined)
    authHelper.removeAuth()
    setCurrentUser(undefined)
  }, [])

  useEffect(() => {
    const syncAuth = (event: Event) => {
      setAuth((event as CustomEvent<AuthModel | undefined>).detail)
    }
    window.addEventListener(authHelper.AUTH_CHANGED_EVENT, syncAuth)
    return () => window.removeEventListener(authHelper.AUTH_CHANGED_EVENT, syncAuth)
  }, [])

  useEffect(() => {
    const validateSharedSession = () => {
      if (document.visibilityState === 'hidden') return
      getCurrentUser()
        .then(({data}) => setCurrentUser(data))
        .catch(() => logout())
    }
    window.addEventListener('focus', validateSharedSession)
    document.addEventListener('visibilitychange', validateSharedSession)
    return () => {
      window.removeEventListener('focus', validateSharedSession)
      document.removeEventListener('visibilitychange', validateSharedSession)
    }
  }, [logout, setCurrentUser])

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {logout, setCurrentUser} = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  useEffect(() => {
    let cancelled = false

    const restoreSession = async () => {
      try {
        if (!authHelper.getAuth()?.access_token) await authHelper.refreshAuth()
        const {data} = await getCurrentUser()
        if (!cancelled && data) {
          setCurrentUser(data)
        }
      } catch (error) {
        console.error(error)
        if (!cancelled) {
          logout()
        }
      } finally {
        if (!cancelled) setShowSplashScreen(false)
      }
    }

    restoreSession()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
